import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { useSEO } from '../../hooks/useSEO';

const DataPolicyPage: React.FC = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en/');

  const TEXT_JA = {
    seo: {
      title: 'データ利用規約 | 3Dプリンター フィラメント試験データの利用について',
      description: '当サイトで公開している3Dプリンター用フィラメントの試験データ（曲げ強度、応力-ひずみ曲線等）の利用規約です。個人利用は自由ですが、公開・再配布・転載・動画/記事掲載等を行う場合は必ず事前にお問い合わせください。',
      keywords: 'データ利用規約,利用規約,フィラメント試験データ,曲げ強度データ,応力ひずみ曲線,データ利用,転載,再配布'
    },
    breadcrumb: { home: 'ホーム', title: 'データ利用規約' },
    title: 'データ利用規約',
    subtitle: '当サイトで公開している3Dプリンター用フィラメントの試験データ（曲げ強度、応力-ひずみ曲線、CSV等）の利用に関する規約です。',
    sections: {
      policy: {
        title: '基本方針',
        p1: '当サイトで公開している試験データは、3Dプリンター愛好家の皆様の材料選定や知識向上に役立てていただくことを目的としています。',
        p2: '個人利用は自由',
        p3: 'ですが、',
        p4: '公開・再配布・転載等を行う場合は必ず事前にお問い合わせください。'
      },
      personal: {
        title: '個人利用について（許可不要）',
        intro: '以下の用途であれば、',
        intro2: '事前の許可なく自由にご利用いただけます',
        intro3: '：',
        items: [
          '個人的な材料選定の参考',
          '自身の3Dプリンター設定の最適化',
          '個人的な学習・研究目的での閲覧・保存',
          '社内・チーム内での情報共有（非公開の範囲内）'
        ],
        note: '※ただし、データの正確性を保証するものではありません。利用は自己責任でお願いします。'
      },
      public: {
        title: '公開・再配布について（要事前連絡）',
        intro: '以下の行為を行う場合は、',
        intro2: '必ず事前にお問い合わせください',
        intro3: '：',
        items: [
          '動画・記事・SNS等での公開',
          '（YouTube、ブログ、Twitter/X、Instagram等）',
          'データの転載・引用',
          '（グラフのスクリーンショット、数値の掲載等）',
          'CSVファイルの再配布',
          '（ダウンロードしたデータの第三者への提供）',
          '商用利用',
          '（製品カタログ、販売資料、広告等への掲載）',
          '二次加工したデータの公開',
          '（グラフの再作成、データの編集・加工後の公開）'
        ],
        contact: {
          title: 'お問い合わせ方法',
          intro: '公開・転載等をご希望の場合は、',
          link: 'お問い合わせフォーム',
          intro2: 'より以下の情報をお知らせください：',
          items: [
            '利用目的（動画、記事、SNS投稿等）',
            '公開先（YouTube、ブログURL、SNSアカウント等）',
            '使用するデータ（フィラメント名、グラフ種類等）'
          ],
          note: '※通常、非営利目的であれば許可いたしますが、出典明記等の条件をお願いする場合があります。'
        }
      },
      prohibited: {
        title: '禁止事項',
        intro: '以下の行為は',
        intro2: '固く禁止',
        intro3: 'します：',
        items: [
          '無断での商用利用',
          'データの改ざん・虚偽の情報との組み合わせ',
          '当サイトの信用を毀損する目的での利用',
          '第三者の権利を侵害する目的での利用',
          '法令に違反する目的での利用'
        ]
      },
      disclaimer: {
        title: '免責事項',
        items: [
          '当サイトのデータは参考情報として提供しており、正確性・完全性を保証するものではありません。',
          '試験条件、測定環境、個体差等により、実際の性能は異なる場合があります。',
          'データの利用により生じた損害について、当サイトは一切の責任を負いません。',
          'データは予告なく変更・削除される場合があります。'
        ]
      },
      changes: {
        title: '規約の変更',
        text: '当規約は予告なく変更される場合があります。変更後の規約は、本ページに掲載した時点で効力を生じるものとします。'
      }
    },
    buttons: {
      contact: 'お問い合わせフォームへ',
      home: 'ホームに戻る'
    }
  };

  const TEXT_EN = {
    seo: {
      title: 'Data Usage Policy | 3D Printer Filament Test Data Usage Terms',
      description: 'Usage policy for 3D printer filament test data (bending strength, stress-strain curves, etc.) published on this site. Personal use is free, but please contact us in advance for publication, redistribution, reprinting, video/article posting, etc.',
      keywords: 'data usage policy,terms of use,filament test data,bending strength data,stress strain curve,data usage,reprint,redistribution'
    },
    breadcrumb: { home: 'Home', title: 'Data Usage Policy' },
    title: 'Data Usage Policy',
    subtitle: 'Terms of use for 3D printer filament test data (bending strength, stress-strain curves, CSV, etc.) published on this site.',
    sections: {
      policy: {
        title: 'Basic Policy',
        p1: 'The test data published on this site is intended to help 3D printer enthusiasts with material selection and knowledge improvement.',
        p2: 'Personal use is free',
        p3: ', but ',
        p4: 'please contact us in advance for publication, redistribution, reprinting, etc.'
      },
      personal: {
        title: 'About Personal Use (No Permission Required)',
        intro: 'For the following purposes, ',
        intro2: 'you can use freely without prior permission',
        intro3: ':',
        items: [
          'Reference for personal material selection',
          'Optimization of your own 3D printer settings',
          'Viewing and saving for personal learning and research purposes',
          'Information sharing within company/team (within non-public scope)'
        ],
        note: '* However, we do not guarantee the accuracy of the data. Please use at your own risk.'
      },
      public: {
        title: 'About Publication/Redistribution (Prior Contact Required)',
        intro: 'If you wish to perform the following actions, ',
        intro2: 'please contact us in advance',
        intro3: ':',
        items: [
          'Publication in videos, articles, SNS, etc.',
          '(YouTube, blogs, Twitter/X, Instagram, etc.)',
          'Reprinting or quoting data',
          '(screenshots of graphs, posting numerical values, etc.)',
          'Redistribution of CSV files',
          '(providing downloaded data to third parties)',
          'Commercial use',
          '(posting in product catalogs, sales materials, advertisements, etc.)',
          'Publication of processed data',
          '(recreating graphs, publishing after editing/processing data)'
        ],
        contact: {
          title: 'Contact Method',
          intro: 'If you wish to publish or reprint, please provide the following information via the ',
          link: 'contact form',
          intro2: ':',
          items: [
            'Purpose of use (video, article, SNS post, etc.)',
            'Publication destination (YouTube, blog URL, SNS account, etc.)',
            'Data to be used (filament name, graph type, etc.)'
          ],
          note: '* Normally, we will grant permission for non-commercial purposes, but we may request conditions such as source attribution.'
        }
      },
      prohibited: {
        title: 'Prohibited Actions',
        intro: 'The following actions are ',
        intro2: 'strictly prohibited',
        intro3: ':',
        items: [
          'Unauthorized commercial use',
          'Falsification of data or combination with false information',
          'Use for the purpose of damaging the credibility of this site',
          'Use for the purpose of infringing on the rights of third parties',
          'Use for purposes that violate laws and regulations'
        ]
      },
      disclaimer: {
        title: 'Disclaimer',
        items: [
          'The data on this site is provided as reference information and does not guarantee accuracy or completeness.',
          'Actual performance may differ depending on test conditions, measurement environment, individual differences, etc.',
          'This site assumes no responsibility for any damage caused by the use of the data.',
          'Data may be changed or deleted without notice.'
        ]
      },
      changes: {
        title: 'Changes to Terms',
        text: 'These terms may be changed without notice. Revised terms shall take effect when posted on this page.'
      }
    },
    buttons: {
      contact: 'Go to Contact Form',
      home: 'Back to Home'
    }
  };

  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;

  useSEO({
    title: TEXT.seo.title,
    description: TEXT.seo.description,
    keywords: TEXT.seo.keywords,
    canonicalPath: isEnglish ? '/en/data-policy' : '/data-policy'
  });

  const breadcrumbItems = [
    { label: TEXT.breadcrumb.home, path: isEnglish ? '/en' : '/' },
    { label: TEXT.breadcrumb.title }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="mt-8 space-y-8">
            {/* タイトル */}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                {TEXT.title}
              </h1>
              <div className="section-divider max-w-md mx-auto mb-6"></div>
              <p className="text-metal-light max-w-3xl mx-auto leading-relaxed">
                {TEXT.subtitle}
              </p>
            </div>

            {/* 基本方針 */}
            <div className="metal-panel p-6">
              <h2 className="text-xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2 flex items-center gap-2">
                <i className="ri-file-text-line text-accent"></i>
                {TEXT.sections.policy.title}
              </h2>
              <div className="space-y-4 text-metal-light leading-relaxed">
                <p>{TEXT.sections.policy.p1}</p>
                <p>
                  <strong className="text-white">{TEXT.sections.policy.p2}</strong>
                  {TEXT.sections.policy.p3}
                  <strong className="text-accent">{TEXT.sections.policy.p4}</strong>
                </p>
              </div>
            </div>

            {/* 個人利用について */}
            <div className="metal-panel p-6">
              <h2 className="text-xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2 flex items-center gap-2">
                <i className="ri-user-line text-accent"></i>
                {TEXT.sections.personal.title}
              </h2>
              <div className="space-y-3 text-metal-light">
                <p>
                  {TEXT.sections.personal.intro}
                  <strong className="text-white">{TEXT.sections.personal.intro2}</strong>
                  {TEXT.sections.personal.intro3}
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {TEXT.sections.personal.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
                <p className="text-sm mt-4">{TEXT.sections.personal.note}</p>
              </div>
            </div>

            {/* 公開・再配布について */}
            <div className="metal-panel p-6 border-2 border-accent/50">
              <h2 className="text-xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2 flex items-center gap-2">
                <i className="ri-alert-line text-accent"></i>
                {TEXT.sections.public.title}
              </h2>
              <div className="space-y-3 text-metal-light">
                <p className="text-accent font-semibold">
                  {TEXT.sections.public.intro}
                  <strong>{TEXT.sections.public.intro2}</strong>
                  {TEXT.sections.public.intro3}
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {TEXT.sections.public.items.map((item, i) => (
                    <li key={i}>
                      <strong className="text-white">{item}</strong>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 p-4 bg-metal/50 rounded border border-accent/30">
                  <p className="text-white font-semibold mb-2">
                    <i className="ri-mail-line text-accent mr-2"></i>
                    {TEXT.sections.public.contact.title}
                  </p>
                  <p className="text-sm">
                    {TEXT.sections.public.contact.intro}
                    <Link to="/contact" className="text-accent hover:text-accent-orange underline transition-colors">
                      {TEXT.sections.public.contact.link}
                    </Link>
                    {TEXT.sections.public.contact.intro2}
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-4 mt-2 text-sm">
                    {TEXT.sections.public.contact.items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-metal-light mt-3">
                    {TEXT.sections.public.contact.note}
                  </p>
                </div>
              </div>
            </div>

            {/* 禁止事項 */}
            <div className="metal-panel p-6">
              <h2 className="text-xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2 flex items-center gap-2">
                <i className="ri-forbid-line text-accent-orange"></i>
                {TEXT.sections.prohibited.title}
              </h2>
              <div className="space-y-3 text-metal-light">
                <p>
                  {TEXT.sections.prohibited.intro}
                  <strong className="text-accent-orange">{TEXT.sections.prohibited.intro2}</strong>
                  {TEXT.sections.prohibited.intro3}
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {TEXT.sections.prohibited.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 免責事項 */}
            <div className="metal-panel p-6">
              <h2 className="text-xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2 flex items-center gap-2">
                <i className="ri-information-line text-accent"></i>
                {TEXT.sections.disclaimer.title}
              </h2>
              <div className="space-y-3 text-metal-light text-sm">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  {TEXT.sections.disclaimer.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 規約の変更 */}
            <div className="metal-panel p-6">
              <h2 className="text-xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2 flex items-center gap-2">
                <i className="ri-refresh-line text-accent"></i>
                {TEXT.sections.changes.title}
              </h2>
              <div className="space-y-3 text-metal-light text-sm">
                <p>{TEXT.sections.changes.text}</p>
              </div>
            </div>

            {/* お問い合わせ */}
            <div className="text-center">
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent hover:bg-accent-orange text-graphite font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line"></i>
                {TEXT.buttons.contact}
              </Link>
            </div>

            {/* 戻るリンク */}
            <div className="text-center">
              <Link 
                to={isEnglish ? '/en' : '/'}
                className="inline-flex items-center gap-2 text-accent hover:text-accent-orange font-medium transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                {TEXT.buttons.home}
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DataPolicyPage;